import genId from '#src/client/common/utils/genId.js';
import wait from '#src/client/common/utils/time/wait.js';
import errorToObj from '#src/client/common/utils/errorToObj.js';
import { createStore, produce } from 'solid-js/store';

let store = {};
let setStore = () => { };

const update = (callback) => {
    setStore.apply(null, [produce(callback)]);
};

// Augment as they are added.
export const addNotificationErrors = (errors) => {
    update((state) => {
        for (const error of errors) {
            state.errors.push({
                isOpen: true,
                areDetailsVisible: false,
                hasBeenSubmitted: false,
                value: errorToObj(error),
            });
        }
    });
};

// Just hide them initially, until the animation has had a chance to remove them from the screen.
export const dismissAllNotificationErrors = async () => {
    update((state) => {
        for (const error of state.errors) {
            error.isOpen = false;
        }
    });

    await wait(2000);

    update((state) => {
        state.errors = state.errors.filter((error) => {
            return error.isOpen;
        });
    });
};

export const dismissNotificationErrorAt = async (index) => {
    const dismissedId = genId('dismissedError');

    update((state) => {
        state.errors[index].id = dismissedId;
        state.errors[index].isOpen = false;
    });

    await wait(2000);

    // Might have already been cleared since the animation started.
    update((state) => {
        const newIndex = state.errors.findIndex((error) => {
            return error.id === dismissedId;
        });

        if (newIndex !== -1) {
            state.errors.splice(newIndex, 1);
        }
    });
};

export const updateNotificationErrorAt = (index, callback) => {
    update((state) => {
        callback(state.errors[index]);
    });
};

export const getNotificationErrors = () => {
    return store.errors;
};


export const successStates = {
    created: 'created',
    appearing: 'appearing',
    dismissing: 'dismissing',
};

export const showNotificationSuccess = async (message, timer) => {
    const isValidString = Object.prototype.toString.call(message) === '[object String]' && message.length > 0;
    message = isValidString ? message : 'Saved Successfully!';

    const id = genId('success');

    update((state) => {
        state.success = {
            id: id,
            state: successStates.created,
            value: message,
        };
    });

    // Give it enough time to create the element.
    await wait(1);

    update((state) => {
        if (state.success?.id === id && successStates.created) {
            state.success.state = successStates.appearing;
        }
    });

    // Show message for 3 seconds by default.
    await wait(timer ?? 3000);

    update((state) => {
        if (state.success?.id === id && successStates.appearing) {
            state.success.state = successStates.dismissing;
        }
    });

    // Enough time for the animation to complete.
    await wait(500);

    update((state) => {
        if (state.success?.id === id && successStates.dismissing) {
            state.success = null;
        }
    });
};

export const dismissNotificationSuccess = async () => {
    const dismissedId = genId('dismissedSuccess');

    update((state) => {
        // Only dismiss if we are in the appearing state, not if it's still in the process of entering or already leaving.
        if (state.success.state === successStates.appearing) {
            state.success.id = dismissedId;
            state.success.state = successStates.dismissing;
        }
    });

    // Enough time for the animation to complete.
    await wait(500);

    update((state) => {
        if (state.success?.id === dismissedId && successStates.dismissing) {
            state.success = null;
        }
    });
};

// Director contains its own store, so it can always be called from anywhere so long as setup was ever called.
export const setup = () => {
    const [setupStore, setupSetStore] = createStore({
        errors: [],
        success: null,
    });

    store = setupStore; // eslint-disable-line solid/reactivity
    setStore = setupSetStore;

    return [store, setStore];
};


export default {
    addNotificationErrors,
    dismissAllNotificationErrors,
    dismissNotificationErrorAt,
    updateNotificationErrorAt,

    successStates,
    showNotificationSuccess,
    dismissNotificationSuccess,

    setup,
    getNotificationErrors,
};